import * as stylex from "@stylexjs/stylex";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import "@/design/tokens.stylex";
import { colors, radii, shadows, space } from "@/design/tokens.stylex";
import { useEscapeLayer } from "./useEscapeLayer";

// An overlay anchored to a trigger: portalled to the body, kept inside the
// viewport, flipped above the trigger when there is no room below, and closed
// by an outside click or Escape. Menus, chip popovers and pickers build on it.
// Escape closes only the top overlay, so a menu inside a side panel or dialog
// closes without taking the panel or dialog with it.

const styles = {
  panel: {
    kVAEAm: "xixxii4",
    kY2c9j: "x1aciyhk",
    k7Eaqz: "xfb3i0g",
    kORKVm: "x1odjw0f",
    kWkggS: "x1h1f4i9",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xzbo3x7",
    kaIpWk: "x16l1o21",
    kGVxlE: "x1n1ksyb",
    kmVPX3: "xt5iall",
    $$css: true
  }
};
export interface PopoverProps {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: (reason: "outside" | "escape") => void;
  align?: "start" | "end";
  /** Match the trigger's width at least. */
  matchWidth?: boolean;
  /** The tallest the panel grows before it scrolls. */
  maxHeight?: number;
  role?: string;
  id?: string;
  "aria-label"?: string;
  xstyle?: stylex.StyleXStyles;
  children: ReactNode;
}
interface Position {
  top: number;
  left: number;
  minWidth?: number;
  maxHeight: number;
}
const GAP = 6;
const EDGE = 8;
export default function Popover({
  open,
  anchorRef,
  onClose,
  align = "start",
  matchWidth = false,
  maxHeight: tallest = 420,
  role,
  id,
  "aria-label": ariaLabel,
  xstyle,
  children
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({
    top: -9999,
    left: -9999,
    maxHeight: 360
  });
  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    const panel = panelRef.current;
    if (!rect || !panel) return;
    const width = Math.max(panel.offsetWidth, matchWidth ? rect.width : 0);
    const height = panel.scrollHeight;
    const below = window.innerHeight - rect.bottom - GAP - EDGE;
    const above = rect.top - GAP - EDGE;
    const flip = below < Math.min(height, tallest, 360) && above > below;
    const left = align === "end" ? rect.right - width : rect.left;
    setPosition({
      top: flip ? Math.max(EDGE, rect.top - GAP - Math.min(height, above)) : rect.bottom + GAP,
      left: Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE)),
      minWidth: matchWidth ? rect.width : undefined,
      maxHeight: Math.max(Math.min(120, tallest), Math.min(tallest, flip ? above : below))
    });
  }, [align, anchorRef, matchWidth, tallest]);
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);
  useEscapeLayer(() => onClose("escape"), open);
  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      const node = event.target as Node;
      if (!panelRef.current?.contains(node) && !anchorRef.current?.contains(node)) onClose("outside");
    };
    document.addEventListener("mousedown", outside);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", outside);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef, onClose, open, place]);
  if (!open) return null;
  return createPortal(<div ref={panelRef} id={id} role={role} aria-label={ariaLabel} {...stylex.props(styles.panel, xstyle)} style={position}>
      {children}
    </div>, document.body);
}